import { useEffect, useMemo, useRef, useState } from "react";
import SimpleReactValidator from "simple-react-validator";

import { Box, Button, Collapse, Divider, Grid, IconButton, Stack, TextField, Typography } from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import { DAYS } from "Constants/App";

import DayPicker from "Components/DayPicker";
import TimePicker from "Components/TimePicker";
import StationPicker from "./stationPicker";

export interface IProps {
  data: { [key: string]: any } | null;
  setData: (value: any) => void;
  text: string;
  valueKey: string;
  label: string;
  activeDays?: number[];
  selectStation?: boolean;
}
interface IInput {
  name: string;
  label: string;
  type: string;
  size: number;
}
interface IDayTime {
  start_time?: string;
  end_time?: string;
  interval?: string;
  price?: string;
  eta?: string;
}
export interface IDay {
  id: number;
  times: IDayTime[];
}

export default function Time({ data, setData, text, valueKey, label, activeDays, selectStation }: IProps) {
  const [days, setDays] = useState<IDay[]>(data?.data || []);
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [selectedStation, setSelectedStation] = useState<number>();
  const [formData, setFormData] = useState<IDayTime>({});
  const [dayFormData, setDayFormData] = useState<IDayTime>({});
  const [activeCollapse, setActiveCollapse] = useState<number | null>(null);
  const [update, forceUpdate] = useState(false);

  // const { stations } = useAppSelector((state) => state.stations);

  const Inputs: IInput[] = [
    { name: "start_time", label: "از ساعت", type: "time", size: 2 },
    { name: "end_time", label: "تا ساعت", type: "time", size: 2 },
    { name: valueKey, label, type: "number", size: 2.6 },
  ];

  const validator = useRef(
    new SimpleReactValidator({
      messages: {
        required: "این فیلد الزامی است",
      },
    })
  );

  // Logic functions
  const handleFormData = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDayFormData = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    setDayFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddDays = () => {
    if (
      selectedDays.length === 0 ||
      !formData.start_time ||
      !formData.end_time ||
      !(formData.interval || formData.price || formData.eta)
    ) {
      validator.current.showMessages();
      forceUpdate(!update);
      return;
    }
    if (selectedDays.length > 0 && formData) {
      const daysId = days.map((day) => day.id);

      selectedDays.forEach((day) => {
        if (daysId.includes(day)) {
          setDays((prev) => prev.map((i) => (i.id === day ? { ...i, times: [...i.times, formData] } : i)));
        } else {
          setDays((prev) => [...prev, { id: day, times: [formData] }]);
        }
      });
      setFormData({});
      setSelectedDays([]);
      validator.current.hideMessages();
      forceUpdate(!update);
    }
  };

  const handleAddDaysItem = (dayId: number) => {
    if (!dayFormData.start_time || !dayFormData.end_time || !(dayFormData.interval || dayFormData.price)) {
      // validator2.current.showMessages();
      // forceUpdate(!update);
      return;
    }
    setDays((prev) => prev.map((day) => (day.id === dayId ? { id: day.id, times: [...day.times, dayFormData] } : day)));
    setDayFormData({});
  };

  const handleRemoveDaysItem = (dayId: number, itemIndex: number) => {
    const times = days.filter((day) => day.id === dayId)[0].times.filter((time, index) => index !== itemIndex);
    setDays((prev) => prev.map((day) => (day.id === dayId ? { id: day.id, times } : day)));
  };

  // UI functions
  const handleDayName = (dayId: number) => {
    return DAYS.filter((day) => day.id === dayId)[0].name;
  };

  const renderDaysCard = useMemo(() => {
    return (
      <Stack spacing={1}>
        {days.map((day) => {
          const { times } = day;
          const isOpen = activeCollapse === day.id;

          return (
            <Box sx={{ border: "1px solid #79747E", p: 2, borderRadius: 1 }} key={day.id}>
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item xs={2}>
                  <Typography fontWeight="600">{handleDayName(day.id)}</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography fontWeight="600" className="fa-number">{`${times.length} ${text}`}</Typography>
                </Grid>
                <Grid item>
                  <IconButton onClick={() => setActiveCollapse((prev) => (prev === day.id ? null : day.id))}>
                    {isOpen ? <KeyboardArrowUpIcon /> : <EditOutlinedIcon />}
                  </IconButton>
                </Grid>
              </Grid>

              <Collapse in={activeCollapse === day.id}>
                {times.map((time, timeIdx) => (
                  <Grid container columnSpacing={1} mt={2}>
                    {Inputs.map((input) =>
                      input.type === "time" ? (
                        <Grid item xs={2}>
                          <TimePicker
                            label={input.label}
                            size="small"
                            data={time}
                            name={input.name}
                            setData={(value) => setFormData((prev) => ({ ...prev, [input.name]: value }))}
                            disabled
                          />
                        </Grid>
                      ) : (
                        <Grid item xs={3}>
                          <TextField
                            fullWidth
                            type={input.type}
                            label={input.label}
                            size="small"
                            name={input.name}
                            value={time[`${input.name}` as keyof typeof time]}
                            disabled
                          />
                        </Grid>
                      )
                    )}
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="ایستگاه"
                        size="small"
                        name="source_station_id"
                        value={time["source_station_id" as keyof typeof time]}
                        disabled
                      />
                    </Grid>

                    <Grid item xs={2}>
                      <IconButton onClick={() => handleRemoveDaysItem(day.id, timeIdx)}>
                        <DeleteOutlineOutlinedIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}

                <Grid container columnSpacing={1} mt={2}>
                  {Inputs.map((input) =>
                    input.type === "time" ? (
                      <Grid item xs={3}>
                        <TimePicker
                          label={input.label}
                          size="small"
                          data={dayFormData}
                          name={input.name}
                          setData={(value) => setDayFormData((prev) => ({ ...prev, [input.name]: value }))}
                        />
                      </Grid>
                    ) : (
                      <Grid item xs={3}>
                        <TextField
                          fullWidth
                          type="text"
                          label={input.label}
                          value={dayFormData[`${valueKey}` as keyof typeof dayFormData] || ""}
                          size="small"
                          name={valueKey}
                          onChange={handleDayFormData}
                        />
                      </Grid>
                    )
                  )}
                  <Grid item xs={3}>
                    <Button variant="contained" fullWidth sx={{ height: "100%" }} onClick={() => handleAddDaysItem(day.id)}>
                      افزودن
                    </Button>
                  </Grid>
                </Grid>
                {/* <Grid container columnSpacing={1}>
                  <Grid item xs={3} sx={{ mt: "5px" }}>
                    <Typography variant="caption" sx={{ color: "red" }}>
                      htrh
                      {validator2.current.message("start_time", formData.start_time, "required")}
                    </Typography>
                  </Grid>
                  <Grid item xs={3} sx={{ mt: "5px" }}>
                    <Typography variant="caption" sx={{ color: "red" }}>
                      htrgtntrh
                      {validator2.current.message("end_time", formData.end_time, "required")}
                    </Typography>
                  </Grid>
                  <Grid item xs={3} sx={{ mt: "5px" }}>
                    <Typography variant="caption" sx={{ color: "red" }}>
                      {valueKey === "interval"
                        ? validator2.current.message("interval", formData.interval, "required")
                        : validator2.current.message("price", formData.price, "required")}
                    </Typography>
                  </Grid>
                </Grid> */}
              </Collapse>
            </Box>
          );
        })}
      </Stack>
    );
  }, [days, activeCollapse, dayFormData]);

  useEffect(() => {
    setData(days);
  }, [days]);

  return (
    <>
      <Grid container columnSpacing={1}>
        <Grid item xs={3.4}>
          <DayPicker
            fullWidth
            size="small"
            multiple
            value={selectedDays}
            setData={setSelectedDays}
            label="روزها"
            activeDays={activeDays}
            padding="7px"
            minWidth={30}
          />
        </Grid>

        {Inputs.map((input) =>
          input.type === "time" ? (
            <Grid item xs={input.size}>
              <TimePicker
                label={input.label}
                size="small"
                data={formData}
                name={input.name}
                setData={(value) => {
                  setFormData((prev) => ({ ...prev, [input.name]: value }));
                }}
              />
            </Grid>
          ) : (
            <Grid item xs={input.size}>
              <TextField
                fullWidth
                type="text"
                label={input.label}
                value={formData[`${valueKey}` as keyof typeof formData] || ""}
                size="small"
                name={valueKey}
                onChange={handleFormData}
              />
            </Grid>
          )
        )}
        <Grid item xs={2}>
          <Button variant="contained" fullWidth sx={{ height: "100%" }} onClick={handleAddDays}>
            افزودن
          </Button>
        </Grid>
        {selectStation ? (
          <Grid item xs={3} sx={{ marginTop: "15px" }}>
            <StationPicker
              fullWidth
              size="small"
              value={selectedStation}
              setData={setSelectedStation}
              label="انتخاب ایستگاه"
              activeDays={activeDays}
              setFormData={setFormData}
            />
          </Grid>
        ) : null}
      </Grid>
      {/* <Grid container columnSpacing={1}>
        <Grid item xs={3} sx={{ mt: "5px" }}>
          <Typography variant="caption" sx={{ color: "red" }}>
            {validator.current.message("selectedDays", selectedDays, "required")}
          </Typography>
        </Grid>
        <Grid item xs={2} sx={{ mt: "5px" }}>
          <Typography variant="caption" sx={{ color: "red" }}>
            {validator.current.message("start_time", formData.start_time, "required")}
          </Typography>
        </Grid>
        <Grid item xs={2} sx={{ mt: "5px" }}>
          <Typography variant="caption" sx={{ color: "red" }}>
            {validator.current.message("end_time", formData.end_time, "required")}
          </Typography>
        </Grid>
        <Grid item xs={3} sx={{ mt: "5px" }}>
          <Typography variant="caption" sx={{ color: "red" }}>
            {valueKey === "interval"
              ? validator.current.message("interval", formData.interval, "required")
              : validator.current.message("price", formData.price, "required")}
          </Typography>
        </Grid>
      </Grid> */}
      <Divider sx={{ my: 2 }} />

      <Box sx={{ height: "300px", overflowY: "scroll", pr: 1 }}>
        {days.length === 0 ? <Typography align="center">داده ای موجود نیست</Typography> : renderDaysCard}
      </Box>
    </>
  );
}
